import { KnownCapability, ScopeLevel } from "@/types/capability.types";
import { hasCapability } from "@/utils/capability";

import { useActiveScopeCapabilities } from "../capability/useActiveScopeCapabilities";

// The partner (and partner-group) owner scope (league > organization > platform,
// see PartnersPage's context dispatch) matches the active scope level 1:1, so the
// keys are looked up by the active level and checked against that level's own
// capability set. Keys are scope-exact on purpose — the backend enforces
// `<LEVEL>_PARTNER:*` at the owner node without implied-capability expansion, so
// accepting ancestor-level keys here would light up actions the API then denies.
const PARTNER_READ_KEYS: Record<ScopeLevel, KnownCapability> = {
  platform: "PLATFORM_PARTNER:READ",
  organization: "ORG_PARTNER:READ",
  league: "LEAGUE_PARTNER:READ",
};

const PARTNER_WRITE_KEYS: Record<ScopeLevel, KnownCapability> = {
  platform: "PLATFORM_PARTNER:WRITE",
  organization: "ORG_PARTNER:WRITE",
  league: "LEAGUE_PARTNER:WRITE",
};

/**
 * Permissions for the partners + partner-groups surfaces at the active scope
 * (`<LEVEL>_PARTNER:READ` to view the lists, `<LEVEL>_PARTNER:WRITE` to
 * create/edit/delete/reorder partners and to mutate partner groups). Strict RBAC
 * — the backend resolver is the single source of truth and the S0 grants give
 * every legacy member the matching keys. See docs/capabilities.md.
 */
export const usePartnerPermissions = () => {
  const { level, capabilities } = useActiveScopeCapabilities();

  return {
    canSeePartners: hasCapability(capabilities, PARTNER_READ_KEYS[level]),
    canManagePartners: hasCapability(capabilities, PARTNER_WRITE_KEYS[level]),
  };
};
